import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useProposeImmediateMeeting } from "@/hooks/useImmediateMeetings";
import { Loader2, Video, Info } from "lucide-react";

interface RequestMeetingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mentorshipRequestId: string;
  threadId: string;
  mentorName: string;
}

export function RequestMeetingDialog({
  open,
  onOpenChange,
  mentorshipRequestId,
  threadId,
  mentorName,
}: RequestMeetingDialogProps) {
  const [duration, setDuration] = useState<string>("30");
  const [note, setNote] = useState("");
  const proposeMeeting = useProposeImmediateMeeting();

  const handleSubmit = async () => {
    await proposeMeeting.mutateAsync({
      mentorshipRequestId,
      threadId,
      duration: parseInt(duration),
      note: note.trim() || undefined,
    });
    onOpenChange(false);
    setNote("");
    setDuration("30");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Video className="h-5 w-5" />
            Request a Meeting
          </DialogTitle>
          <DialogDescription>
            Request a Zoom meeting with {mentorName}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              Mentors may prefer to continue asynchronously. They can accept, decline, or suggest continuing via messages.
            </AlertDescription>
          </Alert>

          <div className="space-y-2">
            <Label>Preferred Duration</Label>
            <RadioGroup value={duration} onValueChange={setDuration} className="flex gap-4">
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="15" id="req-d15" />
                <Label htmlFor="req-d15" className="font-normal cursor-pointer">15 min</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="30" id="req-d30" />
                <Label htmlFor="req-d30" className="font-normal cursor-pointer">30 min</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="45" id="req-d45" />
                <Label htmlFor="req-d45" className="font-normal cursor-pointer">45 min</Label>
              </div>
            </RadioGroup>
          </div>

          <div className="space-y-2">
            <Label htmlFor="req-note">Why would a meeting be helpful? (Optional)</Label>
            <Textarea
              id="req-note"
              placeholder="Explain what you'd like to discuss that might benefit from a live conversation..."
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="resize-none"
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={proposeMeeting.isPending}>
            {proposeMeeting.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Video className="h-4 w-4 mr-2" />
            )}
            Send Request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
